import { getZrmLeaseInfo, getZrmListingInfo, getZrmUserInfo } from '@zg-rentals/clickstream';
import { trackEvent } from '../../../client/analytics';

import type { ZrmClickstreamLease, ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

const EVENT_IDS = {
  VIEW_DASHBOARD_TAB: '6043',
  WHEN_LISTING_EXPIRE_LEARN_MORE: '7333',
  VIEW_RESTRICTED_LISTING_BANNER: '7544',
  CLICK_CONTACT_SUPPORT_FROM_RESTRICTED_BANNER: '7545',
} as const;

const BLOCKED_LISTING_TRIGGER_OBJECTS = {
  VERIFY_LISTING: 'verify_your_listing',
  VERIFICATION_LISTING: 'verification_of_your_listing',
  CONTACT_SUPPORT: 'contact_support',
} as const;

// https://dataportal.zillowgroup.net/event-registry/6043
const ViewDashboardTab = ({ user, tab }: { user: ZrmClickstreamUser; tab: string }) =>
  trackEvent.user({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.VIEW_DASHBOARD_TAB,
      event_type_version_id: '3',
    },
    clickstream_trigger: {
      trigger_location_nm: `zrm_properties_${tab}`,
      trigger_type_nm: 'view',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: `zrm_properties_${tab}`,
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/7333
const WhenListingExpireLearnMoreClick = ({
  user,
  currentProperty,
  lease,
}: {
  user: ZrmClickstreamUser;
  currentProperty: ZrmClickstreamListing;
  lease?: ZrmClickstreamLease;
}) =>
  trackEvent.leaseListingUser({
    envelope: {
      event_template_id: '177',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.WHEN_LISTING_EXPIRE_LEARN_MORE,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_property_overview',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'active_listing',
      trigger_source_nm: 'learn_more_listing_expiration',
    },
    semantic: {
      semantic_event_nm: 'learn_more_listing_expiration',
      topic_tag_txt: [],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(currentProperty),
    ...getZrmLeaseInfo(lease),
  });

// https://dataportal.zillowgroup.net/event-registry/7544
const ViewRestrictedListingBanner = ({
  user,
  currentProperty,
  triggerLocation,
}: {
  user: ZrmClickstreamUser;
  currentProperty: ZrmClickstreamListing;
  triggerLocation: 'posting_path_review' | 'zrm_property_overview';
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.VIEW_RESTRICTED_LISTING_BANNER,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: triggerLocation,
      trigger_type_nm: 'impression',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'view_blocked_listing_banner',
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: ['zrm_account_verification'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(currentProperty),
  });

// https://dataportal.zillowgroup.net/event-registry/7545
const ClickContactSupportFromRestrictedBanner = ({
  user,
  currentProperty,
  triggerLocation,
  triggerObject,
}: {
  user: ZrmClickstreamUser;
  currentProperty: ZrmClickstreamListing;
  triggerLocation: 'posting_path_review' | 'zrm_property_overview';
  triggerObject: (typeof BLOCKED_LISTING_TRIGGER_OBJECTS)[keyof typeof BLOCKED_LISTING_TRIGGER_OBJECTS];
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CLICK_CONTACT_SUPPORT_FROM_RESTRICTED_BANNER,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: triggerLocation,
      trigger_type_nm: 'interaction',
      trigger_object_nm: triggerObject,
      trigger_source_nm: 'link_to_contact_support_blocked_banner',
    },
    semantic: {
      semantic_event_nm: 'click_contact_support',
      topic_tag_txt: ['zrm_account_verification'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(currentProperty),
  });

const DashboardCSEvents = {
  ViewDashboardTab,
  WhenListingExpireLearnMoreClick,
  ViewRestrictedListingBanner,
  ClickContactSupportFromRestrictedBanner,
  BLOCKED_LISTING_TRIGGER_OBJECTS,
};

// Clickstream visualizer data-eventid attributes
ViewDashboardTab.data_event_id = EVENT_IDS.VIEW_DASHBOARD_TAB;
WhenListingExpireLearnMoreClick.data_event_id = EVENT_IDS.WHEN_LISTING_EXPIRE_LEARN_MORE;
ViewRestrictedListingBanner.data_event_id = EVENT_IDS.VIEW_RESTRICTED_LISTING_BANNER;
ClickContactSupportFromRestrictedBanner.data_event_id = EVENT_IDS.CLICK_CONTACT_SUPPORT_FROM_RESTRICTED_BANNER;

export default DashboardCSEvents;
